import type { ElementType, HTMLAttributes, ReactNode } from 'react';
import { cx, sizeClass, type Breakpoint, type Colour, type Hue, type Opacity } from './shared';

export type CardFade = 'down' | 'up' | 'left' | 'right' | 'down-left' | 'down-right' | 'up-left' | 'up-right';
export type CardSize = 'xs' | 'sm' | 'base' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';

export interface CardProps extends HTMLAttributes<HTMLElement> {
  /** Element to render: div (default), article, section, a, … */
  as?: ElementType;
  href?: string;
  /** Padding, gaps, title and text size. */
  size?: CardSize;
  /** Background colour: any palette colour. None = transparent. */
  bg?: Colour;
  /** Background opacity, 0–100 in steps of 5 (default 100). The text stays solid. */
  bgOpacity?: Opacity;
  /** Blur what shows through the background: true (6px), 'sm' (3px) or 'lg' (12px). */
  glass?: boolean | 'sm' | 'lg';
  /** Border width: true (1px), 'md' (2px), 'lg' (3px), 'xl' (4px). */
  border?: boolean | 'md' | 'lg' | 'xl';
  /** Border colour: any palette colour (gives a 1px border on its own). Default white at 10%. */
  borderColor?: Colour;
  /** The background fades in this direction, to a quarter of its opacity. */
  fade?: CardFade;
  /** With fade: fade all the way to transparent. */
  fadeFull?: boolean;
  /** Eyebrow, icon, badge and CTA colour. Default cyan. */
  accent?: Hue;
  /** Dark text, for light opaque backgrounds. */
  textDark?: boolean;
  /** Side by side; with a breakpoint, stacked below it and a row from it up. */
  row?: boolean | Breakpoint;
  hover?: boolean;
  /** Spans the full row of a grid. */
  wide?: boolean;
  /** Lines between header, body and footer. */
  divided?: boolean;
  children?: ReactNode;
}

/** The class list for a card, e.g. for markup built outside React. */
export function cardClasses(
  { size, bg, bgOpacity, glass, border, borderColor, fade, fadeFull, accent, textDark, row, hover, wide, divided }: Omit<CardProps, 'as' | 'children'>,
  className?: string,
) {
  return cx(
    'card',
    sizeClass('card', size),
    bg && `card--bg-${bg}`,
    bgOpacity !== undefined && `card--bg-o-${bgOpacity}`,
    glass === true && 'card--glass',
    typeof glass === 'string' && `card--glass-${glass}`,
    border === true && 'card--border',
    typeof border === 'string' && `card--border-${border}`,
    borderColor && `card--border-${borderColor}`,
    fade && `card--fade-${fade}`,
    fadeFull && 'card--fade-full',
    accent && `card--accent-${accent}`,
    textDark && 'card--text-dark',
    row === true && 'card--row',
    typeof row === 'string' && `card--row-${row}`,
    hover && 'card--hover',
    wide && 'card--wide',
    divided && 'card--divided',
    className,
  );
}

export function Card({
  as: Element = 'div',
  size,
  bg,
  bgOpacity,
  glass,
  border,
  borderColor,
  fade,
  fadeFull,
  accent,
  textDark,
  row,
  hover,
  wide,
  divided,
  className,
  ...rest
}: CardProps) {
  return (
    <Element
      className={cardClasses(
        { size, bg, bgOpacity, glass, border, borderColor, fade, fadeFull, accent, textDark, row, hover, wide, divided },
        className,
      )}
      {...rest}
    />
  );
}

type PartProps = HTMLAttributes<HTMLElement> & { as?: ElementType };

function part(base: string, defaultElement: ElementType) {
  const Part = ({ as: Element = defaultElement, className, ...rest }: PartProps) => (
    <Element className={cx(base, className)} {...rest} />
  );
  Part.displayName = base;
  return Part;
}

export const CardHeader = part('card__header', 'header');
export const CardHeading = part('card__heading', 'div');
export const CardActions = part('card__actions', 'div');
export const CardBody = part('card__body', 'div');
export const CardFooter = part('card__footer', 'footer');
export const CardEyebrow = part('card__eyebrow', 'span');
export const CardTitle = part('card__title', 'h3');
export const CardText = part('card__text', 'p');
export const CardDetail = part('card__detail', 'span');
export const CardIcon = part('card__icon', 'span');
export const CardBadge = part('card__badge', 'div');
export const CardBadgeIcon = part('card__badge-icon', 'span');
export const CardBadgeTitle = part('card__badge-title', 'span');
export const CardBadgeText = part('card__badge-text', 'span');

/** Call to action, e.g. "View game →". */
export function CardCta({ children, className, ...rest }: HTMLAttributes<HTMLSpanElement>) {
  return (
    <span className={cx('card__cta', className)} {...rest}>
      {children}
      <span className="card__cta-arrow" aria-hidden="true">
        →
      </span>
    </span>
  );
}

export function CardMedia({ full, className, ...rest }: HTMLAttributes<HTMLDivElement> & { full?: boolean }) {
  return <div className={cx('card__media', full && 'card__media--full', className)} {...rest} />;
}
